import type { GeneratedUnit, TRowConfigFormData, UnitRow } from '@/types/hangar-units'
import type { TRowTypeConfig } from '@/types/hangars'

/**
 * Generate unit layout for a T-hangar row (single or nested/teeth layout)
 * Calculates positions for each unit based on template dimensions
 */
export function generateTRowUnits(config: TRowConfigFormData): GeneratedUnit[] {
  const { layout = 'single' } = config

  if (layout === 'nested') {
    return generateNestedTRowUnits(config)
  }

  return generateSingleRowUnits(config)
}

/**
 * Generate single-row T-hangar layout
 */
function generateSingleRowUnits(config: TRowConfigFormData): GeneratedUnit[] {
  const units: GeneratedUnit[] = []
  const { unit_count, door_width, depth, tail_width, nesting_style } = config

  // Calculate spacing between units based on nesting style
  // Standard: units are side-by-side
  // Offset: units overlap slightly for more efficient use of space
  const unitSpacing = nesting_style === 'offset' ? door_width * 0.9 : door_width

  for (let i = 0; i < unit_count; i++) {
    const sequence = i + 1
    const unitCode = `T-${String(sequence).padStart(2, '0')}`

    // Calculate X position (horizontal along the row)
    const offset_x = i * unitSpacing

    // Y position is constant for all units in a row (facing the taxilane)
    const offset_y = 0

    units.push({
      unit_code: unitCode,
      sequence,
      door_width,
      depth,
      tail_width,
      offset_x,
      offset_y,
      row: 'bottom',
    })
  }

  return units
}

/**
 * Generate nested T-hangar layout (teeth pattern - two rows facing each other)
 * Bottom row units face up, top row units face down
 * Taxilane runs between the two rows
 */
function generateNestedTRowUnits(config: TRowConfigFormData): GeneratedUnit[] {
  const units: GeneratedUnit[] = []
  const { unit_count, door_width, depth, tail_width, nesting_style, taxilane_width } = config

  // For nested layout, split units between two rows
  // If odd number, bottom row gets the extra unit
  const unitsPerRow = config.units_per_row ?? Math.ceil(unit_count / 2)
  const bottomRowCount = unitsPerRow
  const topRowCount = unit_count - bottomRowCount

  // Calculate spacing between units based on nesting style
  const unitSpacing = nesting_style === 'offset' ? door_width * 0.9 : door_width

  // Generate bottom row (facing up toward taxilane)
  for (let i = 0; i < bottomRowCount; i++) {
    const sequence = i + 1
    const unitCode = `B-${String(sequence).padStart(2, '0')}`

    units.push({
      unit_code: unitCode,
      sequence,
      door_width,
      depth,
      tail_width,
      offset_x: i * unitSpacing,
      offset_y: 0, // Bottom row at y=0
      row: 'bottom' as UnitRow,
    })
  }

  // Generate top row (facing down toward taxilane)
  for (let i = 0; i < topRowCount; i++) {
    const sequence = bottomRowCount + i + 1
    const unitCode = `T-${String(i + 1).padStart(2, '0')}`

    units.push({
      unit_code: unitCode,
      sequence,
      door_width,
      depth,
      tail_width,
      offset_x: i * unitSpacing,
      offset_y: depth + taxilane_width, // Top row after taxilane
      row: 'top' as UnitRow,
    })
  }

  return units
}

/**
 * Create TRowTypeConfig from form data
 */
export function createTRowConfig(formData: TRowConfigFormData): TRowTypeConfig {
  return {
    unit_count: formData.unit_count,
    nesting_style: formData.nesting_style,
    layout: formData.layout ?? 'single',
    unit_template: {
      door_width: formData.door_width,
      depth: formData.depth,
      tail_width: formData.tail_width,
    },
    shared_taxilane_width: formData.taxilane_width,
  }
}

/**
 * Calculate overall hangar dimensions from T-row config
 * Used to auto-populate hangar width/depth from unit configuration
 */
export function calculateTRowDimensions(config: TRowConfigFormData): {
  width: number
  depth: number
} {
  const { unit_count, door_width, depth: unitDepth, nesting_style, taxilane_width, layout = 'single' } = config

  // Calculate spacing between units based on nesting style
  const unitSpacing = nesting_style === 'offset' ? door_width * 0.9 : door_width

  if (layout === 'nested') {
    // Nested: two rows facing each other with taxilane in middle
    const unitsPerRow = Math.ceil(unit_count / 2)
    const width = (unitsPerRow - 1) * unitSpacing + door_width
    // Depth = bottom row + taxilane + top row
    const depth = unitDepth * 2 + taxilane_width
    return { width, depth }
  }

  // Single row: width is sum of all unit widths, depth is unit + taxilane
  const width = (unit_count - 1) * unitSpacing + door_width
  const depth = unitDepth + taxilane_width

  return { width, depth }
}

/**
 * Status colors for SVG visualization
 */
export const UNIT_STATUS_COLORS = {
  available: '#22c55e', // green-500
  leased: '#3b82f6', // blue-500
  maintenance: '#f97316', // orange-500
}

/**
 * Get fill color for a unit based on status
 */
export function getUnitColor(status: string, isSelected: boolean): string {
  if (isSelected) {
    return '#8b5cf6' // violet-500 for selected
  }
  return UNIT_STATUS_COLORS[status as keyof typeof UNIT_STATUS_COLORS] || UNIT_STATUS_COLORS.available
}
